import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { toast } from 'react-toastify';
import { z } from 'zod';

import { SuricattaTrainingFormSchema } from '@/constants/formSchema/SuricattaTrainingFormSchema';
import { TrainingTypeEnum } from '@/constants/trainingTypeOptions';
import api from '@/services/apiSgft';
import { Option } from '@/types/Filter';

import ControlledSimpleSelect from '../atoms/ControlledSimpleSelect';
import SearchSelect from '../atoms/SearchSelect';
import TrashButton from '../atoms/TrashButton';

const trainingTypeOptions = [
	{
		value: TrainingTypeEnum.REGULATORY,
		label: TrainingTypeEnum.REGULATORY,
		disabled: false,
	},
	{
		value: TrainingTypeEnum.PROCEDURE,
		label: TrainingTypeEnum.PROCEDURE,
		disabled: false,
	},
];
interface SuricattaTrainingSectionProps {
	index: number;
	form: UseFormReturn<z.infer<typeof SuricattaTrainingFormSchema>>;
	remove: (index: number) => void;
}

export const SuricattaTrainingSection = ({
	index,
	form,
	remove,
}: SuricattaTrainingSectionProps) => {
	const control = form.control;

	const [trainingSearch, setTrainingSearch] = useState('');
	const trainingTypeWatch = form.watch(`sgftTrainings.${index}.trainingType`);
	const selectedTrainings = form.watch(`sgftTrainings`);
	const getTrainings = async () => {
		try {
			const response = await api.get('/trainings', {
				params: {
					getAll: false,
					trainingType: [trainingTypeWatch],
				},
			});
			return response.data.map((el: { id: number; name: string }) => ({
				value: el.id,
				label: el.name,
			}));
		} catch (e) {
			toast.error('Erro ao carregar treinamentos!', {
				theme: 'colored',
				toastId: 'error',
			});
		}
	};

	const { data: trainings } = useQuery<Option[]>(
		['get-all-trainings', trainingTypeWatch],
		getTrainings,
		{
			enabled: !!trainingTypeWatch,
		},
	);

	const availableTrainings = trainings?.filter((el) => {
		return !selectedTrainings.some(
			(selectedTraining, selectedIndex) =>
				selectedTraining.id === el.value && selectedIndex !== index,
		);
	});

	const filteredTrainings =
		availableTrainings?.filter((el: Option) =>
			el.label.toLowerCase().includes(trainingSearch.toLowerCase()),
		) ?? [];

	return (
		<div className="flex w-full items-center justify-between">
			<div className="flex w-full flex-col gap-1 rounded-md bg-gray-100 px-2 py-3">
				<div className="flex w-full items-center justify-between">
					<ControlledSimpleSelect
						title="Tipo de Treinamento"
						width="17.4rem"
						required
						values={trainingTypeOptions}
						control={control}
						{...form.register(`sgftTrainings.${index}.trainingType`)}
					/>
					<SearchSelect
						{...form.register(`sgftTrainings.${index}.id`)}
						control={form.control}
						title="Nome do Treinamento"
						options={filteredTrainings}
						required
						className="w-[25rem]"
						setSearch={(e) => setTrainingSearch(e)}
					/>
					<div className="mt-3">
						<TrashButton onClick={() => remove(index)} color="action" />
					</div>
				</div>
			</div>
		</div>
	);
};
